import { useEffect, useRef, useCallback, RefObject } from 'react';
import { scrollEvents, ScrollToElementEvent, ScrollSource } from '../internal/scroll-event-bus';

/**
 * Scroll Detection Hook (PUBLIC API)
 * 
 * WHY: Allows content sections to participate in the sticky-tab-bar scroll system.
 * 
 * USAGE:
 * - Window pages: useScrollDetection('window')
 * - Modals: useScrollDetection('modal', modalContentRef)
 * 
 * WHAT IT DOES:
 * - Subscribes to scroll events from the sticky-tab-bar
 * - Performs the actual scrolling when tabs are clicked
 * - Handles both window and container-based scrolling
 * - Validates elements are in the correct context
 * 
 * ARCHITECTURE:
 * This is the ONLY public API for external consumers.
 * The internal event bus is an implementation detail.
 * 
 * @example
 * // In a page component
 * function AddressPage() {
 *   useScrollDetection('window');
 *   return <div>...</div>;
 * }
 * 
 * @example
 * // In a modal component
 * function AddressModal() {
 *   const modalRef = useRef<HTMLDivElement>(null);
 *   useScrollDetection('modal', modalRef);
 *   return <div ref={modalRef}>...</div>;
 * }
 */

const SCROLL_TIMEOUT_FALLBACK = 1500;

export function useScrollDetection(source: ScrollSource, scrollContainerRef?: RefObject<HTMLElement>) {
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const callbackRef = useRef<(() => void) | null>(null);
  const hasScrollEndSupport = useRef<boolean>(
    typeof window !== 'undefined' && 'onscrollend' in window
  );

  const handleScrollEnd = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    if (callbackRef.current) {
      callbackRef.current();
      callbackRef.current = null;
    }
  }, []);

  // Set up scrollend event listener
  useEffect(() => {
    if (!hasScrollEndSupport.current) return;

    const scrollTarget = scrollContainerRef?.current || window;
    scrollTarget.addEventListener('scrollend', handleScrollEnd);

    return () => {
      scrollTarget.removeEventListener('scrollend', handleScrollEnd);
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [handleScrollEnd, scrollContainerRef]);

  const handleScrollRequest = useCallback(({ element, offset = 0, onComplete }: ScrollToElementEvent): boolean => {
    // Clear any existing timeout/callback
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    callbackRef.current = onComplete || null;

    const scrollContainer = scrollContainerRef?.current;
    
    console.log('[useScrollDetection] Handling scroll request', {
      elementId: element.id,
      offset,
      hasContainer: !!scrollContainer,
      containerTag: scrollContainer?.tagName,
      containerClass: scrollContainer?.className,
    });
    
    if (scrollContainer) {
      // Custom scroll container (e.g., modal)
      // IMPORTANT: Check if the element is actually inside this container
      // When a modal is open, there might be duplicate IDs (background page + modal)
      const isElementInContainer = scrollContainer.contains(element);
      
      if (!isElementInContainer) {
        console.warn('[useScrollDetection] Element not in container, ignoring scroll request', {
          elementId: element.id,
          containerClass: scrollContainer.className,
        });
        return false;
      }
      
      const containerRect = scrollContainer.getBoundingClientRect();
      const elementRect = element.getBoundingClientRect();
      const scrollTop = scrollContainer.scrollTop;
      const targetScrollTop = scrollTop + elementRect.top - containerRect.top - offset;
      
      console.log('[useScrollDetection] Scrolling modal container', {
        currentScrollTop: scrollTop,
        targetScrollTop,
        elementTop: elementRect.top,
        containerTop: containerRect.top,
        offset,
      });
      
      scrollContainer.scrollTo({
        top: targetScrollTop,
        behavior: 'smooth',
      });
    } else {
      // Default window scroll
      console.log('[useScrollDetection] Scrolling window with offset:', offset);
      const elementTop = element.getBoundingClientRect().top + window.scrollY;
      window.scrollTo({
        top: elementTop - offset,
        behavior: 'smooth',
      });
    }

    // Fallback timeout for browsers without scrollend support
    if (!hasScrollEndSupport.current && onComplete) {
      timeoutRef.current = setTimeout(() => {
        handleScrollEnd();
      }, SCROLL_TIMEOUT_FALLBACK);
    }
    
    return true;
  }, [handleScrollEnd, scrollContainerRef]);

  useEffect(() => {
    return scrollEvents.subscribe(source, handleScrollRequest);
  }, [source, handleScrollRequest]);
}
